import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Key,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { User } from '../../types/auth';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatDate } from '../../utils/dateUtils';
import { UserModal } from './UserModal';
import { DeleteConfirmModal } from '../Common/DeleteConfirmModal';

export const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'super_admin' | 'admin' | 'user'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  // Statistics state
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalAccounts: 0,
    avgAccountsPerUser: 0
  });

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Success/Error messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        per_page: perPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (roleFilter !== 'all') {
        params.role = roleFilter;
      }

      if (statusFilter !== 'all') {
        params.is_active = statusFilter === 'active';
      }

      const response = await apiService.getUsers(params);
      setUsers(response.data);
      setTotalPages(response.meta.last_page);
      setTotalUsers(response.meta.total);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch with high per_page to get accurate aggregate counts
      const statsParams: any = {
        per_page: 10000, // High number to get all records with counts
      };

      if (roleFilter !== 'all') {
        statsParams.role = roleFilter;
      }

      if (statusFilter !== 'all') {
        statsParams.is_active = statusFilter === 'active';
      }

      const statsResponse = await apiService.getUsers(statsParams);
      const allUsers = statsResponse.data;
      const total = statsResponse.meta.total;

      // Calculate active users count based on current filter
      let activeUsersCount = 0;
      if (statusFilter === 'active') {
        // If filtering for active, all returned users are active
        activeUsersCount = total;
      } else if (statusFilter === 'inactive') {
        // If filtering for inactive, no users are active
        activeUsersCount = 0;
      } else {
        // If showing all, count active users from the data
        activeUsersCount = allUsers.filter((u: User) => u.is_active).length;
      }

      const totalAccounts = allUsers.reduce((sum: number, u: User) => sum + (u.user_accounts_count || 0), 0);

      setStats({
        totalUsers: total,
        activeUsers: activeUsersCount,
        totalAccounts: totalAccounts,
        avgAccountsPerUser: total > 0 ? Math.round(totalAccounts / total) : 0
      });
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch users when page changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, roleFilter, statusFilter]);

  // Fetch stats only when filters change (not on page change)
  useEffect(() => {
    fetchStats();
  }, [searchTerm, roleFilter, statusFilter]);

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      setActionLoading(user.id);
      await apiService.toggleUserStatus(user.id);
      setSuccessMessage(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      await fetchUsers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (user: User) => {
    try {
      setActionLoading(user.id);
      const response = await apiService.resetUserPassword(user.id);
      setSuccessMessage(`Password reset successful. Temporary password: ${response.data?.temporary_password || 'N/A'}`);
      setTimeout(() => setSuccessMessage(null), 10000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await apiService.deleteUser(userToDelete.id);
      setSuccessMessage('User deleted successfully');
      await fetchUsers();
      setShowDeleteModal(false);
      setUserToDelete(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const onUserSaved = async () => {
    setShowUserModal(false);
    setEditingUser(null);
    await fetchUsers();
    setSuccessMessage(editingUser ? 'User updated successfully' : 'User created successfully');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'user':
        return 'User';
      default:
        return role;
    }
  };

  const canManageUser = (user: User) => {
    if (currentUser?.role === 'super_admin') return true;
    if (currentUser?.role === 'admin') return user.role === 'user';
    return false;
  };

  const canViewUser = (user: User) => {
    if (currentUser?.role === 'super_admin') return true;
    if (currentUser?.role === 'admin') return user.role !== 'super_admin';
    return user.id === currentUser?.id;
  };

  // Filter users based on role permissions
  const filteredUsers = users.filter(canViewUser);

  if (currentUser?.role === 'user') {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Access Restricted</h3>
          <p className="mt-2 text-sm text-gray-600">
            You don't have permission to access user management.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-xs sm:text-sm text-gray-600">Manage system users and their permissions</p>
            </div>
          </div>
          {currentUser?.role === 'super_admin' && (
            <button
              onClick={handleCreateUser}
              className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center justify-center space-x-2 w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Add User</span>
            </button>
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-green-800">{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {!loading && stats.totalUsers > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4 bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Users</p>
                <p className="text-2xl font-bold text-blue-900">
                  {stats.totalUsers}
                </p>
                <p className="text-xs text-blue-600 mt-1">Across all pages</p>
              </div>
              <Users className="h-8 w-8 text-blue-600 opacity-75" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Active Users</p>
                <p className="text-2xl font-bold text-green-900">
                  {stats.activeUsers}
                </p>
                <p className="text-xs text-green-600 mt-1">Currently active</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600 opacity-75" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Total Accounts</p>
                <p className="text-2xl font-bold text-purple-900">
                  {stats.totalAccounts}
                </p>
                <p className="text-xs text-purple-600 mt-1">Assigned accounts</p>
              </div>
              <Filter className="h-8 w-8 text-purple-600 opacity-75" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Avg per User</p>
                <p className="text-2xl font-bold text-orange-900">
                  {stats.avgAccountsPerUser}
                </p>
                <p className="text-xs text-orange-600 mt-1">Accounts per user</p>
              </div>
              <ToggleRight className="h-8 w-8 text-orange-600 opacity-75" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Role Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as 'all' | 'super_admin' | 'admin' | 'user')}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none text-sm"
            >
              <option value="all">All Roles</option>
              {currentUser?.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accounts
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleDisplay(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <a
                          href={`/accounts?user_id=${user.id}`}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/accounts?user_id=${user.id}`;
                          }}
                        >
                          <div className="font-medium">
                            {user.user_accounts_count || 0} accounts
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.active_user_accounts_count || 0} active
                          </div>
                        </a>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.is_active ? (
                            <>
                              <div className="h-2 w-2 bg-green-400 rounded-full mr-2"></div>
                              <span className="text-sm text-gray-900">Active</span>
                            </>
                          ) : (
                            <>
                              <div className="h-2 w-2 bg-red-400 rounded-full mr-2"></div>
                              <span className="text-sm text-gray-900">Inactive</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login_at
                          ? formatDate(user.last_login_at)
                          : 'Never'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {canManageUser(user) && (
                            <>
                              <button
                                onClick={() => handleEditUser(user)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Edit User"
                              >
                                <Edit className="h-4 w-4" />
                              </button>

                              <button
                                onClick={() => handleToggleUserStatus(user)}
                                disabled={actionLoading === user.id}
                                className={`${user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                                title={user.is_active ? 'Deactivate User' : 'Activate User'}
                              >
                                {actionLoading === user.id ? (
                                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full"></div>
                                ) : user.is_active ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>

                              <button
                                onClick={() => handleResetPassword(user)}
                                disabled={actionLoading === user.id}
                                className="text-blue-600 hover:text-blue-900"
                                title="Reset Password"
                              >
                                <Key className="h-4 w-4" />
                              </button>

                              {user.id !== currentUser?.id && (
                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete User"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <div key={user.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{user.name}</h3>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                        {getRoleDisplay(user.role)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center">
                      {user.is_active ? (
                        <>
                          <div className="h-2 w-2 bg-green-400 rounded-full mr-2"></div>
                          <span className="text-gray-900">Active</span>
                        </>
                      ) : (
                        <>
                          <div className="h-2 w-2 bg-red-400 rounded-full mr-2"></div>
                          <span className="text-gray-900">Inactive</span>
                        </>
                      )}
                    </div>
                    <span className="text-gray-500">
                      Last login: {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
                    </span>
                  </div>

                  {canManageUser(user) && (
                    <div className="flex items-center justify-center space-x-4 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-900 text-xs"
                      >
                        <Edit className="h-3 w-3" />
                        <span>Edit</span>
                      </button>

                      <button
                        onClick={() => handleToggleUserStatus(user)}
                        disabled={actionLoading === user.id}
                        className={`flex items-center space-x-1 text-xs ${
                          user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                        }`}
                      >
                        {actionLoading === user.id ? (
                          <div className="animate-spin h-3 w-3 border border-gray-300 border-t-gray-600 rounded-full"></div>
                        ) : user.is_active ? (
                          <EyeOff className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                        <span>{user.is_active ? 'Deactivate' : 'Activate'}</span>
                      </button>

                      <button
                        onClick={() => handleResetPassword(user)}
                        disabled={actionLoading === user.id}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-900 text-xs"
                      >
                        <Key className="h-3 w-3" />
                        <span>Reset Password</span>
                      </button>

                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="flex items-center space-x-1 text-red-600 hover:text-red-900 text-xs"
                        >
                          <Trash2 className="h-3 w-3" />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Page <span className="font-medium">{currentPage}</span> of{' '}
                      <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          user={editingUser}
          onClose={() => {
            setShowUserModal(false);
            setEditingUser(null);
          }}
          onSave={onUserSaved}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <DeleteConfirmModal
          title="Delete User"
          message={`Are you sure you want to delete "${userToDelete.name}"? This action cannot be undone.`}
          onConfirm={confirmDeleteUser}
          onCancel={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
        />
      )}
    </div>
  );
};